import { Link, useNavigate } from "react-router-dom";
import { useContext, useState, useEffect, useRef } from "react";
import { AuthContext } from "../../context/authcontext";
import { API_BASE_URL, WS_BASE_URL } from "../../environment";
import { getAuthHeaders } from "../../utils/authUtils";
import male from "../../assets/male.png";
import { useLifecyclePageDetection } from "../../hooks/useLifecyclePageDetection";
import TaskDetailModal from "../TaskDetailModal";

interface NotificationType {
  notification_type_id: number;
  notification_type_key: string;
  notification_type_display_name: string;
}

interface User {
  user_id: number;
  full_name: string;
  avatar_url: string | null;
}

interface NotificationChannel {
  notification_channel_id: number;
  notification_channel_key: string;
  notification_channel_display_name: string;
}

interface Notification {
  notification_id: number;
  notification_config: string;
  created_datetime: string;
  is_read: boolean;
  notification_type: NotificationType;
  tenant_id: number;
  user: User;
  type?: string;
  timestamp?: string;
  related_entity_name?: string;
  related_entity_id?: number;
  notification_channel?: NotificationChannel;
}

export default function TaskNotification() {
  const { auth, selectedTenantId } = useContext(AuthContext);
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const websocketRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const fetchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const previousNotificationIdsRef = useRef<Set<number>>(new Set());
  const isInitialLoadRef = useRef<boolean>(true);
  const currentTenantIdRef = useRef<string | null>(null);
  const currentUserIdRef = useRef<number | null>(null);
  const notificationToggleRef = useRef<HTMLAnchorElement | null>(null);

  const [taskDetails, setTaskDetails] = useState<any>(null);
  const [isLoadingTask, setIsLoadingTask] = useState(false);
  const [taskModalVisible, setTaskModalVisible] = useState(false);
  const [notificationQueue, setNotificationQueue] = useState<Notification[]>(
    []
  );

  const userId = auth?.user_id;
  const isLifecyclePage = useLifecyclePageDetection();
  const unreadCount = notifications.filter((n) => !n.is_read).length;

  const parseNotificationConfig = (configString: string) => {
    try {
      const config = JSON.parse(configString);
      if (config.message && config.due_date) {
        const formattedDate = formatTaskDate(config.due_date);
        config.message = config.message.replace(
          /due on [^.]+\./,
          `due on ${formattedDate}.`
        );
      }
      return config;
    } catch (error) {
      return { message: configString, task_id: null, task_title: null };
    }
  };

  const formatTimeAgo = (dateString: string) => {
    if (!dateString) return "Just now";

    const date = new Date(dateString);
    const now = new Date();

    if (isNaN(date.getTime())) {
      return "Just now";
    }

    const diffInMinutes = Math.floor(
      (now.getTime() - date.getTime()) / (1000 * 60)
    );

    if (diffInMinutes < 1) return "Just now";
    if (diffInMinutes < 60) return `${diffInMinutes} minutes ago`;

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours} hours ago`;

    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays} days ago`;
  };

  const formatTaskDate = (dateString: string | null): string => {
    if (!dateString) return "Not set";
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const getNotificationIcon = (notificationTypeKey: string): string => {
    switch (notificationTypeKey?.toUpperCase()) {
      case "TASK":
        return "ti ti-calendar-event";
      case "PROPOSAL":
        return "ti ti-file-text";
      case "CONTRACT":
        return "ti ti-file-certificate";
      default:
        return "ti ti-bell";
    }
  };

  const fetchNotifications = async () => {
    if (!userId || !selectedTenantId) return;

    if (fetchTimeoutRef.current) {
      clearTimeout(fetchTimeoutRef.current);
    }

    fetchTimeoutRef.current = setTimeout(async () => {
      try {
        const response = await fetch(
          `${API_BASE_URL}/notification/?user_id=${userId}&tenant_id=${selectedTenantId}&is_read=false&is_active=true`,
          {
            method: "GET",
            headers: getAuthHeaders(),
          }
        );

        if (response.ok) {
          const data = await response.json();
          const fetchedNotifications = data.results || [];

          const currentIds = new Set<number>(
            fetchedNotifications.map((n: Notification) => n.notification_id)
          );
          const previousIds = previousNotificationIdsRef.current;

          const newNotificationIds = Array.from(currentIds).filter(
            (id: number) => !previousIds.has(id)
          );

          if (!isInitialLoadRef.current && newNotificationIds.length > 0) {
            const newNotifications = fetchedNotifications.filter(
              (n: Notification) => newNotificationIds.includes(n.notification_id)
            );

            newNotifications.forEach(
              (newNotification: Notification, index: number) => {
                setTimeout(() => {
                  setNotificationQueue((prev) => [...prev, newNotification]);
                }, index * 300);
              }
            );
          }

          if (isInitialLoadRef.current) {
            isInitialLoadRef.current = false;
          }

          previousNotificationIdsRef.current = currentIds;

          setNotifications(fetchedNotifications);
        }
      } catch (error) {
        console.error("Error fetching notifications:", error);
      }
    }, 100);
  };

  const markAsRead = async (notificationId: number) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/notification/${notificationId}/`,
        {
          method: "PATCH",
          headers: getAuthHeaders(),
          body: JSON.stringify({ is_read: true }),
        }
      );

      if (response.ok) {
        setNotifications((prev) =>
          prev.map((notification) =>
            notification.notification_id === notificationId
              ? { ...notification, is_read: true }
              : notification
          )
        );
        removeFromQueue(notificationId);
      }
    } catch (error) { }
  };

  const markAllAsRead = async () => {
    if (!userId || !selectedTenantId) return;

    const unreadNotifications = notifications.filter((n) => !n.is_read);
    if (unreadNotifications.length === 0) return;

    try {
      const response = await fetch(`${API_BASE_URL}/notification/mark-all-read/`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({
          tenant_id: selectedTenantId,
          user_id: userId,
        }),
      });

      if (response.ok) {
        setNotifications((prev) =>
          prev.map((notification) => ({ ...notification, is_read: true }))
        );
        setNotificationQueue([]);
      }
    } catch (error) { }
  };

  const fetchTaskDetails = async (taskId: number) => {
    if (!taskId) return;

    setIsLoadingTask(true);
    try {
      const response = await fetch(`${API_BASE_URL}/task/${taskId}/`, {
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        const taskData = await response.json();
        setTaskDetails(taskData);
      }
    } catch (error) {
    } finally {
      setIsLoadingTask(false);
    }
  };

  const removeFromQueue = (notificationId: number) => {
    setNotificationQueue((prev) =>
      prev.filter((n) => n.notification_id !== notificationId)
    );
  };

  const handleNavigation = (notification: Notification) => {
    const typeKey = notification.notification_type?.notification_type_key?.toUpperCase();
    const entityId = notification.related_entity_id;

    if (typeKey === 'TASK') {
      const parsedConfig = parseNotificationConfig(notification.notification_config);
      if (parsedConfig.task_id) {
        fetchTaskDetails(parsedConfig.task_id);
        setTaskModalVisible(true);
      }
    } else if (entityId) {
      switch (typeKey) {
        case 'LEAD':
          navigate(`/leads/${entityId}`);
          break;
        case 'DEAL':
          navigate(`/deals/${entityId}`);
          break;
        case 'PIPELINE':
          navigate(`/pipelines/${entityId}`);
          break;
        case 'PROPOSAL':
          navigate(`/proposals/${entityId}`);
          break;
        case 'CONTRACT':
          navigate(`/contracts/${entityId}`);
          break;
        case 'CONTACT':
          navigate(`/contacts/${entityId}`);
          break;
        case 'COMPANY':
          navigate(`/companies/${entityId}`);
          break;
      }
    }
  };

  const handleNotificationView = (notification: Notification) => {
    markAsRead(notification.notification_id);
    removeFromQueue(notification.notification_id);
    handleNavigation(notification);
  };

  const handleNotificationClose = (notificationId: number) => {
    removeFromQueue(notificationId);
  };

  const refreshNotifications = async () => {
    if (isRefreshing) return;

    setIsRefreshing(true);
    await fetchNotifications();
    setTimeout(() => setIsRefreshing(false), 1000);
  };

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.is_read) {
      markAsRead(notification.notification_id);
    }

    const dropdownElement = document.querySelector(".dropdown-menu.show");
    if (dropdownElement) {
      dropdownElement.classList.remove("show");
    }

    handleNavigation(notification);
  };

  const handleTaskModalClose = () => {
    setTaskModalVisible(false);
    setTaskDetails(null);
  };

  const handleTaskUpdate = () => {
    fetchNotifications();
  };

  const handleRefreshClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    refreshNotifications();
  };

  const handleMarkAsReadClick = (
    e: React.MouseEvent,
    notificationId: number
  ) => {
    e.preventDefault();
    e.stopPropagation();
    markAsRead(notificationId);
  };

  const handleNotificationItemClick = (
    e: React.MouseEvent,
    notification: Notification
  ) => {
    e.preventDefault();
    e.stopPropagation();
    handleNotificationClick(notification);
  };

  useEffect(() => {
    if (!userId || !selectedTenantId) return;

    let isActive = true;

    // Update refs with current values
    currentTenantIdRef.current = selectedTenantId;
    currentUserIdRef.current = userId;

    const connectWebSocket = () => {
      // Use current ref values instead of closure-captured values
      const currentTenantId = currentTenantIdRef.current;
      const currentUserId = currentUserIdRef.current;

      if (!currentTenantId || !currentUserId) return;

      if (websocketRef.current) {
        const wsToClose = websocketRef.current;
        websocketRef.current = null;
        wsToClose.onmessage = null;
        wsToClose.onerror = null;
        wsToClose.onclose = null;
        if (wsToClose.readyState === WebSocket.CONNECTING) {
          wsToClose.onopen = () => {
            try {
              wsToClose.close();
            } catch (_) {}
          };
        } else {
          try {
            wsToClose.close();
          } catch (_) {}
        }
      }

      const wsUrl = WS_BASE_URL.replace(/^https/, "wss");
      const websocketUrl = `${wsUrl}/ws/${currentTenantId}/${currentUserId}`;

      const ws = new WebSocket(websocketUrl);

      ws.onopen = () => { };

      ws.onmessage = (event) => {
        if (!isActive) return;
        try {
          const data = JSON.parse(event.data);

          if (
            data.type === "connection" ||
            data.type === "connection_established"
          ) {
            return;
          }

          if (data.notification_id || data.type === "notification") {
            fetchNotifications();
          }
        } catch (error) {
          console.error("Error: ", error);
        }
      };

      ws.onerror = (error) => { };

      ws.onclose = () => {
        if (!isActive) return;

        reconnectTimeoutRef.current = setTimeout(() => {
          if (
            currentTenantIdRef.current === currentTenantId &&
            currentUserIdRef.current === currentUserId
          ) {
            connectWebSocket();
          }
        }, 30000);
      };

      websocketRef.current = ws;
    };

    connectWebSocket();

    return () => {
      isActive = false;

      if (websocketRef.current) {
        const wsToClose = websocketRef.current;
        websocketRef.current = null;
        wsToClose.onmessage = null;
        wsToClose.onerror = null;
        wsToClose.onclose = null;
        if (wsToClose.readyState === WebSocket.CONNECTING) {
          wsToClose.onopen = () => {
            try {
              wsToClose.close();
            } catch (_) {}
          };
        } else {
          try {
            wsToClose.close();
          } catch (_) {}
        }
      }

      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
    };
  }, [userId, selectedTenantId]);

  useEffect(() => {
    if (userId && selectedTenantId) {
      isInitialLoadRef.current = true;
      previousNotificationIdsRef.current = new Set();
      setNotifications([]);
      setNotificationQueue([]);

      fetchNotifications();
    }
  }, [userId, selectedTenantId]);

  return (
    <li className="nav-item dropdown nav-item-box">
      <Link
        ref={notificationToggleRef}
        to="#"
        className="nav-link"
        data-bs-toggle="dropdown"
        data-bs-auto-close="outside"
      >
        <i className="ti ti-bell" />
        {unreadCount > 0 && (
          <span className="badge rounded-pill">{unreadCount}</span>
        )}
      </Link>
      <div
        className="dropdown-menu dropdown-menu-end notification-dropdown"
        style={{
          minWidth: "400px",
          width: "400px",
          zIndex: "99999",
        }}
      >
        <div className="topnav-dropdown-header">
          <div className="d-flex justify-content-between align-items-center">
            <h4 className="notification-title">Notifications</h4>
            <div className="d-flex gap-2">
              <button
                className="btn btn-sm btn-outline-primary"
                onClick={handleRefreshClick}
                disabled={isRefreshing}
                title="Refresh notifications"
              >
                <i
                  className={`ti ti-refresh ${isRefreshing ? "animate-spin" : ""
                    }`}
                />
              </button>
              {unreadCount > 0 && (
                <button
                  className="btn btn-sm btn-outline-success"
                  onClick={markAllAsRead}
                  title="Mark all as read"
                >
                  <i className="ti ti-checks" />
                </button>
              )}
            </div>
          </div>
        </div>

        <div
          className="noti-content"
          style={{
            width: "100%",
            overflowY: "auto",
            maxHeight: "300px",
          }}
        >
          <ul className="notification-list">
            {notifications.length === 0 ? (
              <li className="notification-message">
                <div className="text-center py-3 text-muted">
                  No notifications available
                </div>
              </li>
            ) : (
              notifications.map((notification) => {
                const parsedConfig = parseNotificationConfig(
                  notification.notification_config
                );
                return (
                  <li
                    key={notification.notification_id}
                    className="notification-message"
                    style={{
                      backgroundColor: !notification.is_read
                        ? "#f8f9ff"
                        : "transparent",
                      borderLeft: !notification.is_read
                        ? "3px solid #007bff"
                        : "none",
                      paddingLeft: !notification.is_read ? "12px" : "15px",
                    }}
                  >
                    <div
                      className="d-flex justify-content-between align-items-start"
                      style={{
                        width: "100%",
                      }}
                    >
                      <Link
                        to="#"
                        className="flex-grow-1"
                        onClick={(e) =>
                          handleNotificationItemClick(e, notification)
                        }
                        style={{ textDecoration: "none", minWidth: 0 }}
                      >
                        <div className="media d-flex">
                          <span className="avatar flex-shrink-0">
                            <img
                              src={notification.user?.avatar_url || male}
                              alt={notification.user?.full_name || "User"}
                              style={{
                                width: "40px",
                                height: "35px",
                                objectFit: "cover",
                                borderRadius: "50%",
                              }}
                            />
                            {!notification.is_read && (
                              <span className="badge badge-info rounded-pill" />
                            )}
                          </span>
                          <div
                            className="media-body flex-grow-1"
                            style={{
                              minWidth: 0,
                              paddingLeft: "12px",
                            }}
                          >
                            <p
                              className="noti-details"
                              style={{
                                wordWrap: "break-word",
                                whiteSpace: "normal",
                                margin: "0 0 4px 0",
                                lineHeight: "1.4",
                              }}
                            >
                              {parsedConfig.message}
                            </p>
                            <p
                              className="noti-time"
                              style={{
                                margin: "0",
                                fontSize: "0.875rem",
                                opacity: "0.7",
                              }}
                            >
                              {formatTimeAgo(notification.created_datetime)}
                            </p>
                          </div>
                        </div>
                      </Link>
                      {!notification.is_read && (
                        <button
                          className="btn btn-sm btn-link text-primary p-0 ms-2 mx-2"
                          onClick={(e) =>
                            handleMarkAsReadClick(
                              e,
                              notification.notification_id
                            )
                          }
                          title="Mark as read"
                        >
                          <i className="ti ti-check" />
                        </button>
                      )}
                    </div>
                  </li>
                );
              })
            )}
          </ul>
        </div>
      </div>

      <TaskDetailModal
        isVisible={taskModalVisible}
        taskDetails={taskDetails}
        isLoading={isLoadingTask}
        onClose={handleTaskModalClose}
        onTaskUpdate={handleTaskUpdate}
        tenantId={selectedTenantId}
      />

      {/* Custom Notification Stack */}
      <div
        id="notification-popup-container"
        style={{
          position: "fixed",
          bottom: "20px",
          right: "20px",
          zIndex: 10000,
          display: "flex",
          flexDirection: "column",
          gap: "12px",
          maxWidth: "400px",
        }}
      >
        {notificationQueue.slice(0, 3).map((notification, index) => {
          const parsedConfig = parseNotificationConfig(
            notification.notification_config
          );
          const iconClass = getNotificationIcon(
            notification.notification_type.notification_type_key
          );

          return (
            <div
              key={notification.notification_id}
              style={{
                background: "white",
                borderRadius: "12px",
                boxShadow:
                  "0 8px 24px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08)",
                padding: "20px",
                minWidth: "380px",
                animation:
                  "slideInFromRight 0.4s ease-out, floatUpDown 2s ease-in-out 0.4s infinite",
                border: "1px solid rgba(0, 0, 0, 0.08)",
                display: "flex",
                alignItems: "flex-start",
                gap: "16px",
                position: "relative",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.animationPlayState = "paused";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.animationPlayState = "running";
              }}
            >
              {/* Icon */}
              <div
                style={{
                  width: "40px",
                  height: "40px",
                  borderRadius: "12px",
                  background: "#824eeb",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginTop: "2px",
                  flexShrink: 0,
                  boxShadow: "0 4px 12px rgba(102, 126, 234, 0.4)",
                }}
              >
                <i
                  className={iconClass}
                  style={{
                    fontSize: "24px",
                    color: "white",
                  }}
                />
              </div>

              {/* Content */}
              <div
                style={{
                  flex: 1,
                  minWidth: 0,
                }}
              >
                <p
                  style={{
                    margin: 0,
                    fontSize: "14.5px",
                    lineHeight: "1.5",
                    color: "#2d3748",
                    fontWeight: 500,
                    marginBottom: "12px",
                  }}
                >
                  {parsedConfig.message || "You have a new notification"}
                </p>

                <button
                  onClick={() => handleNotificationView(notification)}
                  style={{
                    background: "#824eeb",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    padding: "5px 14px",
                    fontSize: "14px",
                    fontWeight: 600,
                    cursor: "pointer",
                    transition: "transform 0.2s, box-shadow 0.2s",
                    boxShadow: "0 2px 8px rgba(102, 126, 234, 0.3)",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-1px)";
                    e.currentTarget.style.boxShadow =
                      "0 4px 12px rgba(102, 126, 234, 0.4)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow =
                      "0 2px 8px rgba(102, 126, 234, 0.3)";
                  }}
                >
                  View{" "}
                  {
                    notification.notification_type
                      .notification_type_display_name
                  }
                </button>
              </div>

              {/* Close Button */}
              <button
                onClick={() =>
                  handleNotificationClose(notification.notification_id)
                }
                style={{
                  position: "absolute",
                  top: "12px",
                  right: "12px",
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  padding: "2px",
                  borderRadius: "6px",
                  transition: "background 0.2s",
                  color: "#a0aec0",
                  fontSize: "16px",
                  lineHeight: 1,
                  width: "28px",
                  height: "28px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "rgba(0, 0, 0, 0.05)";
                  e.currentTarget.style.color = "#2d3748";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "transparent";
                  e.currentTarget.style.color = "#a0aec0";
                }}
              >
                ×
              </button>
            </div>
          );
        })}

        {/* Summary notification for remaining notifications */}
        {notificationQueue.length > 3 && (
          <div
            style={{
              background: "linear-gradient(135deg, #824eeb 0%, #9d6ae8 100%)",
              borderRadius: "12px",
              boxShadow:
                "0 8px 24px rgba(130, 78, 235, 0.3), 0 2px 8px rgba(130, 78, 235, 0.2)",
              padding: "16px 20px",
              minWidth: "380px",
              animation: "slideInFromRight 0.4s ease-out",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              cursor: "pointer",
              transition: "transform 0.2s, box-shadow 0.2s",
            }}
            onClick={() => {
              setNotificationQueue([]);

              // Use setTimeout to ensure state update completes before triggering dropdown
              setTimeout(() => {
                const notificationToggle = notificationToggleRef.current;
                if (notificationToggle) {
                  notificationToggle.click();
                }
              }, 100);
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "scale(1.02)";
              e.currentTarget.style.boxShadow =
                "0 12px 32px rgba(130, 78, 235, 0.4), 0 4px 12px rgba(130, 78, 235, 0.3)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "scale(1)";
              e.currentTarget.style.boxShadow =
                "0 8px 24px rgba(130, 78, 235, 0.3), 0 2px 8px rgba(130, 78, 235, 0.2)";
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                flex: 1,
              }}
            >
              <div
                style={{
                  width: "40px",
                  height: "40px",
                  borderRadius: "50%",
                  background: "rgba(255, 255, 255, 0.2)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <i
                  className="ti ti-bell"
                  style={{
                    fontSize: "20px",
                    color: "white",
                  }}
                />
              </div>
              <div>
                <p
                  style={{
                    margin: 0,
                    fontSize: "15px",
                    fontWeight: 600,
                    color: "white",
                    lineHeight: "1.4",
                  }}
                >
                  You have {notificationQueue.length - 3} more unread
                  notification{notificationQueue.length - 3 !== 1 ? "s" : ""}
                </p>
                <p
                  style={{
                    margin: "4px 0 0 0",
                    fontSize: "13px",
                    color: "rgba(255, 255, 255, 0.9)",
                  }}
                >
                  Click to view all notifications
                </p>
              </div>
            </div>
            <i
              className="ti ti-chevron-right"
              style={{
                fontSize: "20px",
                color: "white",
                opacity: 0.8,
              }}
            />
          </div>
        )}
      </div>

      <style>
        {`
          @keyframes slideInFromRight {
            from {
              transform: translateX(100%);
              opacity: 0;
            }
            to {
              transform: translateX(0);
              opacity: 1;
            }
          }

          @keyframes floatUpDown {
            0%, 100% {
              transform: translateY(0);
            }
            50% {
              transform: translateY(-8px);
            }
          }
        `}
      </style>
    </li>
  );
}
